/**
 * The subscription pricing engine — pure functions, no I/O, no Prisma (same rule `@hrms/payroll`
 * follows for money math, applied here for a different reason: this formula must be computed
 * IDENTICALLY by the API, which charges the authoritative amount at checkout, and by apps/web,
 * which shows a live preview on the Plans & Pricing seat calculator and the Checkout order
 * summary. Two implementations of the same formula is how a demo shows one number and a real
 * charge shows another — this module is imported by both, not reimplemented by either.
 *
 * MONEY IN, MONEY OUT, ALWAYS DECIMAL STRINGS (invariant 2). Decimal.js does the arithmetic
 * internally; every public function accepts and returns `string`, never `number` — the same
 * convention `decimalStringSchema` and `payroll-outputs.ts`'s `netPayable` already use.
 *
 * INDIA-ONLY (INR) THIS SPRINT, DELIBERATELY. An International/USD region is a follow-up, not
 * half-built here — see the billing module's phase-1 scope note. `isInterState` still needs to be
 * threaded through now, though, because CGST+SGST vs IGST is not a India-vs-international split —
 * it is Place-of-Supply-state-vs-home-state, which matters for every India-only customer too.
 *
 * RN-SAFE: no Node built-ins, no I/O, no dates beyond the caller-supplied cycle length.
 */

import Decimal from 'decimal.js';
import type { BillingFrequency, ModuleKey } from './enums.js';
import { computeGstSplit } from './gst.js';

const DISPLAY_PLACES = 2; // INR's minor unit is paise — two decimal places, not four.

/** Months in a billing cycle — the multiplier the monthly `baseFee`/`extraSeatRate` scale by. */
const CYCLE_MONTHS: Record<BillingFrequency, number> = {
  monthly: 1,
  six_month: 6,
  yearly: 12,
};

/** The duration discount: 10% at six months, 20% at a year, per the billing architecture doc. */
const CYCLE_DISCOUNT_PERCENT: Record<BillingFrequency, number> = {
  monthly: 0,
  six_month: 10,
  yearly: 20,
};

const DEFAULT_GST_RATE_PERCENT = '18';

/**
 * Employees a free-trial org (Subscription.status `trialing`) may have besides the owner's own
 * employment. Paid subscriptions are not capped by this. The API enforces it on employee create
 * (`PlanLimitError`); setup-status reports it so the web app can show what is left.
 */
export const FREE_TRIAL_EMPLOYEE_LIMIT = 5;

export type ModulePricingKind = 'per_seat' | 'flat';

export interface ModulePricing {
  readonly kind: ModulePricingKind;
  /** Monthly rate — per seat if `kind` is `'per_seat'`, flat per org if `'flat'`. Decimal string. */
  readonly rate: string;
}

export interface PlanPricingInput {
  /** Monthly, decimal string. Covers `baseSeats` seats. */
  readonly baseFee: string;
  readonly baseSeats: number;
  /** Monthly, decimal string. Charged per seat above `baseSeats`. */
  readonly extraSeatRate: string;
  readonly mandatoryModuleKeys: readonly ModuleKey[];
  /** Non-mandatory modules included by default, each with the rate deducted when toggled off. */
  readonly togglableModules: ReadonlyMap<ModuleKey, ModulePricing>;
}

export interface AddonSelection {
  readonly moduleKey: ModuleKey;
  readonly pricing: ModulePricing;
}

export interface SubscriptionPriceInput {
  readonly plan: PlanPricingInput;
  readonly billingFrequency: BillingFrequency;
  readonly seatCount: number;
  /** Non-mandatory modules the customer switched off. Must be a subset of `plan.togglableModules`'s keys. */
  readonly deselectedModuleKeys: readonly ModuleKey[];
  /** Standalone add-ons bought on top (e.g. a Starter org buying just Expense & Reimbursements). */
  readonly addons?: readonly AddonSelection[];
  /** Defaults to 18 — the only rate this sprint prices against. */
  readonly gstRatePercent?: string;
  /** Place-of-Supply state !== the issuing legal entity's home state — CGST+SGST vs IGST, not an India/international split. */
  readonly isInterState: boolean;
}

export interface PriceLine {
  readonly label: string;
  /** Negative for a deduction (a toggled-off module). */
  readonly amount: string;
}

export interface GstBreakdown {
  readonly cgst: string;
  readonly sgst: string;
  readonly igst: string;
  readonly totalTax: string;
  readonly isInterState: boolean;
  readonly ratePercent: string;
}

export interface SubscriptionPriceBreakdown {
  readonly effectiveModuleKeys: readonly ModuleKey[];
  readonly cycleMonths: number;
  readonly durationDiscountPercent: number;
  readonly lines: readonly PriceLine[];
  readonly subtotalBeforeDiscount: string;
  readonly durationDiscountAmount: string;
  readonly subtotal: string;
  readonly gst: GstBreakdown;
  readonly totalPayable: string;
}

export class PricingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PricingError';
  }
}

function d(value: string): Decimal {
  return new Decimal(value);
}

function money(value: Decimal): string {
  return value.toDecimalPlaces(DISPLAY_PLACES, Decimal.ROUND_HALF_UP).toFixed(DISPLAY_PLACES);
}

function moduleAmountForCycle(pricing: ModulePricing, seatCount: number, cycleMonths: number): Decimal {
  const monthly = pricing.kind === 'per_seat' ? d(pricing.rate).times(seatCount) : d(pricing.rate);
  return monthly.times(cycleMonths);
}

/** CGST+SGST split for an intra-state sale, IGST for inter-state — same total either way, only the
 * split changes, per GST law. Shared by `computeSubscriptionPrice` and `computeSeatProration` so
 * the two can never compute tax differently.
 *
 * Delegates to `./gst.ts`'s `computeGstSplit` — the ONE place this rounding rule is implemented
 * (KEKA_VS_BILLING.md gap #5 / BIL-4). A prior version of this function rounded CGST and SGST to
 * the paisa independently, then separately summed the UNROUNDED values for `totalTax` — so on an
 * odd-paisa total, the displayed components could disagree with the displayed total by a paisa.
 * `computeGstSplit` rounds the total first and derives both components from it, so they always
 * sum to exactly what's printed.
 *
 * `computeGstSplit` takes supplier/place-of-supply STATE STRINGS (reimbursement's shape, derived
 * from a GSTIN) rather than this module's own `isInterState` boolean — it only ever compares them
 * for equality, never reads the values, so two fixed sentinel strings that differ exactly when
 * `isInterState` is true reproduce the same decision without needing a real state on either side. */
function computeGst(subtotal: Decimal, ratePercent: string, isInterState: boolean): GstBreakdown {
  const gstRate = d(ratePercent);
  const split = computeGstSplit({
    taxableAmount: subtotal,
    rate: gstRate,
    supplierState: 'home',
    placeOfSupplyState: isInterState ? 'other' : 'home',
  });
  return {
    cgst: money(split.cgst),
    sgst: money(split.sgst),
    igst: money(split.igst),
    totalTax: money(split.total),
    isInterState,
    ratePercent: gstRate.toFixed(0),
  };
}

/**
 * Computes the full order summary for one checkout or seat/plan change — base fee, extra seats,
 * module toggle deductions, standalone add-ons, the multi-month discount, and the GST split. The
 * caller (a route handler or a web calculator) owns fetching `Plan`/`AddonModule` rows and mapping
 * them into `PlanPricingInput`/`AddonSelection` — this function does no I/O of its own.
 */
export function computeSubscriptionPrice(input: SubscriptionPriceInput): SubscriptionPriceBreakdown {
  const { plan, seatCount, billingFrequency, deselectedModuleKeys, addons = [] } = input;

  if (!Number.isInteger(seatCount) || seatCount < 1) {
    throw new PricingError('seatCount must be a positive whole number');
  }
  const invalidDeselections = deselectedModuleKeys.filter((key) => !plan.togglableModules.has(key));
  if (invalidDeselections.length > 0) {
    throw new PricingError(
      `Cannot deselect a module this plan does not offer as optional: ${invalidDeselections.join(', ')}`,
    );
  }

  const cycleMonths = CYCLE_MONTHS[billingFrequency];
  const durationDiscountPercent = CYCLE_DISCOUNT_PERCENT[billingFrequency];
  const lines: PriceLine[] = [];

  const baseFeeForCycle = d(plan.baseFee).times(cycleMonths);
  lines.push({
    label: `Base Platform Fee (Includes ${plan.baseSeats} seats for ${cycleMonths} month${cycleMonths === 1 ? '' : 's'})`,
    amount: money(baseFeeForCycle),
  });

  const extraSeats = Math.max(0, seatCount - plan.baseSeats);
  const extraSeatAmount = d(plan.extraSeatRate).times(extraSeats).times(cycleMonths);
  if (extraSeats > 0) {
    lines.push({
      label: `Additional Seat Fee (${extraSeats} seats x ${cycleMonths} month${cycleMonths === 1 ? '' : 's'})`,
      amount: money(extraSeatAmount),
    });
  }

  let moduleDeduction = new Decimal(0);
  for (const key of deselectedModuleKeys) {
    const pricing = plan.togglableModules.get(key);
    if (!pricing) continue; // already rejected above; defensive only
    const perCycle = moduleAmountForCycle(pricing, seatCount, cycleMonths);
    moduleDeduction = moduleDeduction.plus(perCycle);
    lines.push({ label: `Module Removed (${key})`, amount: money(perCycle.negated()) });
  }

  let addonTotal = new Decimal(0);
  for (const addon of addons) {
    const perCycle = moduleAmountForCycle(addon.pricing, seatCount, cycleMonths);
    addonTotal = addonTotal.plus(perCycle);
    lines.push({ label: `Add-On (${addon.moduleKey})`, amount: money(perCycle) });
  }

  const subtotalBeforeDiscount = baseFeeForCycle.plus(extraSeatAmount).plus(addonTotal).minus(moduleDeduction);
  const durationDiscountAmount = subtotalBeforeDiscount.times(durationDiscountPercent).dividedBy(100);
  const subtotal = subtotalBeforeDiscount.minus(durationDiscountAmount);

  const gst = computeGst(subtotal, input.gstRatePercent ?? DEFAULT_GST_RATE_PERCENT, input.isInterState);
  const totalPayable = subtotal.plus(d(gst.totalTax));

  const effectiveModuleKeys = [
    ...plan.mandatoryModuleKeys,
    ...[...plan.togglableModules.keys()].filter((key) => !deselectedModuleKeys.includes(key)),
    ...addons.map((addon) => addon.moduleKey),
  ];

  return {
    effectiveModuleKeys: [...new Set(effectiveModuleKeys)],
    cycleMonths,
    durationDiscountPercent,
    lines,
    subtotalBeforeDiscount: money(subtotalBeforeDiscount),
    durationDiscountAmount: money(durationDiscountAmount),
    subtotal: money(subtotal),
    gst,
    totalPayable: money(totalPayable),
  };
}

const DAYS_PER_MONTH = 30; // the "Manage Active Employee Seats" modal's own proration basis.

export interface SeatProrationInput {
  /** How many seats are being ADDED — never negative. A seat decrease charges nothing this sprint
   * (no refund path yet); the caller clamps `max(0, newSeatCount - currentSeatCount)` before this. */
  readonly extraSeatsAdded: number;
  /** Monthly, decimal string — the plan's `extraSeatRate`. */
  readonly extraSeatRate: string;
  /** Days left in the current billing period, inclusive of today. */
  readonly daysRemainingInCycle: number;
  /** The subscription's OWN duration discount — the seat addition is billed on the same terms as
   * the cycle it is joining, mid-cycle, not repriced at the 1-month rate. */
  readonly durationDiscountPercent: number;
  readonly gstRatePercent?: string;
  readonly isInterState: boolean;
}

export interface SeatProrationBreakdown {
  readonly subtotal: string;
  readonly gst: GstBreakdown;
  readonly totalPayable: string;
}

/**
 * Prorates a mid-cycle seat increase — the "Manage Active Employee Seats" modal's own formula:
 * `extraSeatsAdded x extraSeatRate x (daysRemaining / 30) x (1 - durationDiscountPercent)`, then
 * GST on top. A pure function, not folded into `computeSubscriptionPrice`, because it prices a
 * DIFFERENT thing — a partial month against a period that is already running, not a whole cycle.
 */
export function computeSeatProration(input: SeatProrationInput): SeatProrationBreakdown {
  if (!Number.isInteger(input.extraSeatsAdded) || input.extraSeatsAdded < 0) {
    throw new PricingError('extraSeatsAdded must be a non-negative whole number');
  }
  if (input.daysRemainingInCycle < 0) {
    throw new PricingError('daysRemainingInCycle must not be negative');
  }

  const monthlyCharge = d(input.extraSeatRate).times(input.extraSeatsAdded);
  const proratedForDays = monthlyCharge.times(input.daysRemainingInCycle).dividedBy(DAYS_PER_MONTH);
  const afterDiscount = proratedForDays.times(100 - input.durationDiscountPercent).dividedBy(100);
  const subtotal = money(afterDiscount);

  const gst = computeGst(d(subtotal), input.gstRatePercent ?? DEFAULT_GST_RATE_PERCENT, input.isInterState);
  const totalPayable = d(subtotal).plus(d(gst.totalTax));

  return { subtotal, gst, totalPayable: money(totalPayable) };
}
